/**
 * Types for the Claude Code loops control plane.
 *
 * Firestore collection: `loopControls` (DEV project). Holds the *mutable* state of
 * each engineering loop — whether it's enabled, who paused it and why, and a record
 * of its last run. The loop runners on the operator's machine read this at startup
 * (see `.claude/loops/bin/loop-control.sh`) and write `lastRun` when they finish.
 *
 * The *static* metadata for each loop (display name, schedule, group) lives in
 * `LOOP_REGISTRY` below — it's version-controlled, not in Firestore — and the admin
 * UI merges the registry with the live Firestore docs.
 *
 * Two reserved doc ids:
 *   - `_global` → {@link GlobalLoopControl}: the master kill switch.
 *   - `<loopId>` → {@link LoopControl}: per-loop state, keyed by the loop id used by
 *     the runners (e.g. "builder", "reviewer", "sentinel").
 *
 * See docs/loop-instructions/CLAUDE_LOOPS_OPERATING_SYSTEM.md for the loop designs.
 */

/** Outcome of a loop's most recent run, written by the runner. */
export type LoopRunStatus =
  | 'ok' // finished cleanly
  | 'timed_out' // hit the wall-clock cap and was killed
  | 'error' // exited non-zero
  | 'skipped' // skipped (kill switch / disabled / no work)
  | 'running' // currently in flight (optional; runners may not set this)
  | 'unknown';

/** A record of one loop run, persisted to `loopControls/<loopId>.lastRun`. */
export interface LoopRun {
  /** ISO-8601 timestamp of when the run finished (UTC). */
  ts: string;
  status: LoopRunStatus;
  /** Wall-clock duration in minutes. */
  durationMins?: number;
  /** Short human summary (mirrors the Slack line). */
  summary?: string;
  /** Model the run used (e.g. "fable", "opus (fallback)"). */
  model?: string;
}

/** Per-loop mutable control state. Doc id is the loop id. */
export interface LoopControl {
  /** The loop id (matches the doc id and the runner's `$LOOP`). */
  id: string;
  /**
   * Whether the loop is allowed to run. Absent/true ⇒ runs (opt-out model:
   * a loop with no doc yet still runs). Set false to pause it remotely.
   */
  enabled: boolean;
  /** Free-text reason shown when paused (optional). */
  pausedReason?: string;
  /** Who last changed `enabled` (admin email). */
  pausedBy?: string;
  /** Firestore server timestamp of the last `enabled` change. */
  updatedAt?: unknown;
  /** The loop's most recent run, written by the runner. */
  lastRun?: LoopRun;
}

/** The reserved `loopControls/_global` doc: the master kill switch. */
export interface GlobalLoopControl {
  /** When true, ALL loops skip regardless of their individual `enabled`. */
  killAll: boolean;
  /** Who last changed `killAll` (admin email). */
  updatedBy?: string;
  /** Firestore server timestamp of the last change. */
  updatedAt?: unknown;
  /** Optional reason for the global pause. */
  reason?: string;
}

/** Logical grouping for display, mirrors the operating-system blueprint. */
export type LoopGroup = 'unattended' | 'scheduled' | 'interactive' | 'ops';

/** Static, version-controlled metadata for a known loop. */
export interface LoopMeta {
  /** Loop id — must match the runner's `$LOOP` and the Firestore doc id. */
  id: string;
  /** Display name for the admin UI. */
  displayName: string;
  group: LoopGroup;
  /** Human-readable schedule (derived from the launchd plists). */
  schedule: string;
  /**
   * Whether the launchd job is loaded on the operator's Mac (`launchctl list`), i.e. the
   * loop actually fires on the schedule above. `false` entries are kept so the admin UI can
   * show them (retired / on-demand / unloaded) without pretending they run.
   */
  scheduled: boolean;
  /**
   * Which working clone the loop runs from. `none` = the loop owns no clone (pure-Python
   * monitors and the decisions sweep act on Firestore/GitHub only).
   */
  clone: 'enough' | 'builder' | 'reviewer' | 'librarian' | 'none';
  /** One-line description of what the loop does. */
  description: string;
  /**
   * Whether the admin UI exposes an enable/disable toggle. Only honest when the runner
   * consults the control plane (`loop_gate <id>` in `.claude/loops/bin/loop-control.sh`);
   * a loop whose runner does not gate is shown for status only, otherwise the toggle would
   * be a placebo.
   */
  togglable: boolean;
}

/**
 * The known loops. Order is display order in the admin panel.
 * Schedules are the launchd `StartCalendarInterval` / `StartInterval` values from
 * `.claude/loops/launchd/com.enough.claude-loop.<id>.plist`, expressed in the operator's
 * local time (US Eastern). `scheduled` mirrors what is loaded in launchd (verified
 * 2026-09-16: 10 loaded; grooming / cohort-monitor / groomer / usage are not).
 * `loopControls.test.ts` cross-checks every schedule string against its plist.
 */
export const LOOP_REGISTRY: LoopMeta[] = [
  {
    id: 'builder',
    displayName: 'Builder',
    group: 'unattended',
    schedule: 'Mon–Sat 22:00 & Tue–Sun 03:00 ET',
    scheduled: true,
    clone: 'builder',
    description:
      'Implements groomed Kanban cards on claude/* branches and opens draft PRs into dev. One isolated session per card.',
    togglable: true,
  },
  {
    id: 'reviewer',
    displayName: 'Reviewer',
    group: 'unattended',
    schedule: 'Chained after each Builder run + daily 06:00 ET backstop',
    scheduled: true,
    clone: 'reviewer',
    description:
      'Independent verification of Builder PRs against the original card (the ask) — catches scope drift and partial work.',
    togglable: true,
  },
  {
    id: 'sentinel',
    displayName: 'Sentinel',
    group: 'ops',
    schedule: 'Daily 05:00 ET',
    scheduled: true,
    clone: 'enough',
    description: 'Security & ops sweep — Sentry, dependency, and config checks.',
    togglable: true,
  },
  {
    id: 'watchdog',
    displayName: 'Watchdog',
    group: 'ops',
    schedule: 'Daily 06:30 ET',
    scheduled: true,
    clone: 'none',
    description:
      'Deterministic (no-model) monitor of the other loops: run health, PR backlog, back-pressure. Posts the morning digest; a non-zero exit pages Slack.',
    togglable: false,
  },
  {
    id: 'decisions',
    displayName: 'Decisions',
    group: 'ops',
    schedule: 'Every 10 min',
    scheduled: true,
    clone: 'none',
    description:
      'Applies human decisions from admin → Decisions to the board and starts an event-triggered Builder run for cards just approved.',
    togglable: true,
  },
  {
    id: 'scoper',
    displayName: 'Scoper',
    group: 'scheduled',
    schedule: 'Every 60 min',
    scheduled: true,
    clone: 'enough',
    description:
      'Feature requirements studio — turns needs-spec cards into groomed, decomposed cards.',
    togglable: true,
  },
  {
    id: 'intake',
    displayName: 'Intake',
    group: 'interactive',
    schedule: 'Every 30 min',
    scheduled: true,
    clone: 'enough',
    description:
      'Two-way Slack intake — routes commands and conversations. Stays reachable even when work loops are paused.',
    togglable: false,
  },
  {
    id: 'analyst',
    displayName: 'Analyst',
    group: 'scheduled',
    schedule: 'Mon 06:00 ET',
    scheduled: true,
    clone: 'enough',
    description:
      'Read-only Monday data brief to #claude-loops — KPI deltas, what shipped vs whether it moved, data-backed priorities.',
    togglable: true,
  },
  {
    id: 'librarian',
    displayName: 'Librarian',
    group: 'scheduled',
    schedule: 'Sat 10:00 ET',
    scheduled: true,
    clone: 'librarian',
    description:
      'Weekly docs/memory pass (draft claude/docs-* PR) plus at most one chat-knowledge card.',
    togglable: true,
  },
  {
    id: 'chat-qa',
    displayName: 'Chat QA',
    group: 'scheduled',
    schedule: 'Tue 00:00 ET',
    scheduled: true,
    clone: 'enough',
    description:
      'Weekly Chat Coach QA sweep — exercises the chat backend and posts a report. Runner does not consult the control plane, so no toggle.',
    togglable: false,
  },
  {
    id: 'groomer',
    displayName: 'Groomer',
    group: 'scheduled',
    schedule: 'RETIRED 2026-09-16 (unloaded; plist was Sun 21:00 ET)',
    scheduled: false,
    clone: 'enough',
    description:
      'Retired: the Sunday sprint-proposal ceremony is gone. Board hygiene runs in the daily decisions sweep; humans prioritize at the epic level on the board.',
    togglable: true,
  },
  {
    id: 'grooming',
    displayName: 'Monday Grooming',
    group: 'interactive',
    schedule: 'Not scheduled — on demand via /grooming in #claude-loops (plist disabled)',
    scheduled: false,
    clone: 'enough',
    description: 'Interactive grooming session over Slack. Converted from cron to an L12 command 2026-07-13.',
    togglable: true,
  },
  {
    id: 'cohort-monitor',
    displayName: 'Cohort Monitor',
    group: 'ops',
    schedule: 'Not scheduled — plist unloaded (was every 60 min)',
    scheduled: false,
    clone: 'none',
    description:
      'Deterministic monitor for cohort-assignment conflicts. Not loaded in launchd; kept for a manual run.',
    togglable: false,
  },
];

/** Loop ids that the admin UI shows a toggle for. */
export const TOGGLABLE_LOOP_IDS = LOOP_REGISTRY.filter((l) => l.togglable).map((l) => l.id);

/** Loop ids whose launchd job is loaded — the ones that actually fire. */
export const SCHEDULED_LOOP_IDS = LOOP_REGISTRY.filter((l) => l.scheduled).map((l) => l.id);
